import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  message,
  Typography,
  Divider,
  Modal,
  Space,
} from "antd";
import { DeleteOutlined, SaveOutlined } from "@ant-design/icons";

import {
  getPdfConfigAPI,
  savePdfConfigAPI,
  deletePdfConfigAPI,
} from "../../api/pdfApi";

const { Title, Text } = Typography;
const { TextArea } = Input;

const PdfConfig = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [configId, setConfigId] = useState(null);

  // =========================
  // LOAD CONFIG
  // =========================
  const getConfig = async () => {
    try {
      // Your API returns the config object directly (not wrapped in {data: {success}})
      const configData = await getPdfConfigAPI();
      
      console.log("Loaded config data:", configData);
      
      // Check if we have valid config with data
      if (configData && Object.keys(configData).length > 0 && configData.id) {
        setConfigId(configData.id);
        form.setFieldsValue(configData);
        console.log("Config loaded successfully with ID:", configData.id);
      } else {
        // No config found in database
        console.log("No existing config found in database");
        form.resetFields();
        setConfigId(null);
      }
    } catch (error) {
      console.log("Error loading config:", error);
      // Don't show error if it's just empty (first time)
      if (error.response && error.response.status !== 404) {
        message.error("Failed to load PDF configuration");
      }
      form.resetFields();
      setConfigId(null);
    }
  };

  useEffect(() => {
    getConfig();
  }, []);

  // =========================
  // SAVE CONFIG
  // =========================
  const onFinish = async (values) => {
    try {
      setLoading(true);
      
      console.log("Saving config:", values);
      
      // Your save API expects the data directly
      const res = await savePdfConfigAPI(values);
      
      console.log("Save response:", res);
      
      if (res && res.success) {
        message.success(res.message || "PDF Configuration Saved Successfully");
        // Reload config to get the new ID
        await getConfig();
      } else {
        message.error(res?.message || "Failed to save configuration");
      }
      
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Save error:", error);
      message.error(error.response?.data?.message || "Something went wrong");
    }
  };

  // =========================
  // DELETE CONFIG
  // =========================
  const handleDelete = () => {
    if (!configId) {
      message.warning("No configuration found to delete");
      return;
    }

    Modal.confirm({
      title: "Delete Configuration",
      content: "Are you sure you want to delete the PDF configuration? This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setDeleteLoading(true);
          const res = await deletePdfConfigAPI(configId);
          
          console.log("Delete response:", res);
          
          if (res && res.success) {
            message.success(res.message || "PDF Configuration Deleted Successfully");
            // Reset form after deletion
            form.resetFields();
            setConfigId(null);
          } else {
            message.error(res?.message || "Failed to delete configuration");
          }
          setDeleteLoading(false);
        } catch (error) {
          setDeleteLoading(false);
          console.log("Delete error:", error);
          message.error(error.response?.data?.message || "Something went wrong while deleting");
        }
      },
    });
  };

  // =========================
  // RESET FORM
  // =========================
  const handleReset = () => {
    Modal.confirm({
      title: "Reset Form",
      content: "Are you sure you want to reset all fields?",
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        form.resetFields();
        message.info("Form has been reset");
      },
    });
  };

  return (
    <div style={{ padding: 20, background: "#f5f7fb", minHeight: "100vh" }}>
      <Card
        bordered={false}
        style={{
          borderRadius: 18,
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Title level={3}>PDF Configuration</Title>
            <Text type="secondary">
              Manage Header and Footer details for PDF Bills / Reports
            </Text>
          </div>
        {/*{configId && (
            <Button 
              danger 
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              loading={deleteLoading}
              size="large"
            >
              Delete Config
            </Button>
          )} */}
        </div>

        <Divider />

        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          initialValues={{
            hotel_name: "",
            subtitle: "",
            address: "",
            phone_no: "", 
            gst_no: "",
            vat_no: "",
            fssai_no: "",
            footer_title: "",
            footer_subtitle: "",
            terms: "",
          }}
        >
          {/* HEADER */}
          <Title level={5}>Header Details</Title>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="hotel_name"
                label="Hotel Name"
                rules={[{ required: true, message: "Please enter hotel name" }]}
              >
                <Input placeholder="Enter Hotel Name" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="subtitle"
                label="Subtitle"
              >
                <Input placeholder="Bar / Restaurant / Wine Shop" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="address"
                label="Address"
              >
                <TextArea rows={2} placeholder="Enter full address" />
              </Form.Item>
            </Col>

            {/* ADD THIS PHONE NUMBER BLOCK */}
          <Col span={24}>
            <Form.Item
              name="phone_no"
              label="Phone Number"
            >
              <Input placeholder="Enter Phone Number " />
            </Form.Item>
          </Col>


            <Col span={8}>
              <Form.Item
                name="gst_no"
                label="GST No"
              >
                <Input placeholder="Enter GST Number" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="vat_no"
                label="VAT No"
              >
                <Input placeholder="Enter VAT Number" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="fssai_no"
                label="FSSAI No"
              >
                <Input placeholder="Enter FSSAI Number" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* FOOTER */}
          <Title level={5}>Footer Details</Title>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="footer_title"
                label="Footer Title"
              >
                <Input placeholder="Thank You" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="footer_subtitle"
                label="Footer Subtitle"
              >
                <Input placeholder="Visit Again" />
              </Form.Item>
            </Col>

           {/* <Col span={24}>
              <Form.Item
                name="terms"
                label="Terms & Conditions"
              >
                <TextArea rows={3} placeholder="Enter terms and conditions" />
              </Form.Item>
            </Col>*/}
          </Row>

          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              icon={<SaveOutlined />}
              style={{
                borderRadius: 10,
                marginTop: 10,
                background: "#1677ff",
              }}
            >
              Save Configuration
            </Button>
            
            
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default PdfConfig;